import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, message, Space, Divider, Avatar, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LinkOutlined, DisconnectOutlined, SyncOutlined } from '@ant-design/icons';
import { authHeader } from '../../../utils/api';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const CRMAuth = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [crmConnections, setCrmConnections] = useState({});
  const [lastSyncTime, setLastSyncTime] = useState(null);

  // CRM platforms with local logos
  const crmPlatforms = [
    {
      key: 'salesforce',
      name: 'Salesforce',
      logo: '/crm_logos/salesforrce.png',
      color: '#00A1E0',
      description: 'Connect to Salesforce CRM to sync contacts and campaigns'
    },
    {
      key: 'housecall',
      name: 'Housecall Pro',
      logo: '/crm_logos/Housecall.png',
      color: '#FF6B35',
      description: 'Connect to Housecall Pro for field service management'
    },
    {
      key: 'servicetitan',
      name: 'ServiceTitan',
      logo: '/crm_logos/ServiceTitan.png',
      color: '#FF6B35',
      description: 'Connect to ServiceTitan for service business automation'
    },
    {
      key: 'fieldedge',
      name: 'FieldEdge',
      logo: '/crm_logos/FieldEdge.png',
      color: '#2E7D32',
      description: 'Connect to FieldEdge for field service management'
    },
    {
      key: 'zoho',
      name: 'Zoho',
      logo: '/crm_logos/Zoho.png',
      color: '#3371E5',
      description: 'Connect to Zoho CRM for comprehensive business management'
    },
    {
      key: 'hubspot',
      name: 'HubSpot',
      logo: '/crm_logos/hubspot.png',
      color: '#FF7A59',
      description: 'Connect to HubSpot for inbound marketing and sales'
    },
    {
      key: 'gohighlevel',
      name: 'GoHighLevel',
      logo: '/crm_logos/GoHighLevel.png',
      color: '#1E5FD1',
      description: 'Connect to GoHighLevel for marketing automation'
    },
    {
      key: 'nutshell',
      name: 'Nutshell',
      logo: '/crm_logos/Nutshell.png',
      color: '#6B46C1',
      description: 'Connect to Nutshell for simple CRM solution'
    },
    {
      key: 'servicefusion',
      name: 'Service Fusion',
      logo: '/crm_logos/ServiceFusion.png',
      color: '#FF6B35',
      description: 'Connect to Service Fusion for field service management'
    },
    {
      key: 'kickserv',
      name: 'Kickserv',
      logo: '/crm_logos/Kickserv.jpg',
      color: '#2E7D32',
      description: 'Connect to Kickserv for field service software'
    },
    {
      key: 'successware',
      name: 'Successware',
      logo: '/crm_logos/SuccessWare.png',
      color: '#FF6B35',
      description: 'Connect to Successware for service business management'
    }
  ];

  // Auto-sync CRM data on component mount (login/refresh)
  useEffect(() => {
    const loadAndSyncCrmConnections = async () => {
      try {
        const response = await axios.get('/api/clients/me/crm-connections', { 
          headers: authHeader() 
        });
        const connections = response.data || {};
        setCrmConnections(connections);
        
        // Auto-sync all connected CRMs
        const connectedCrms = Object.keys(connections).filter(key => connections[key]?.connected);
        if (connectedCrms.length > 0) {
          await autoSyncAllCrms(connectedCrms);
        }
      } catch (error) {
        // Silently handle 404 - endpoint may not exist yet
        if (error.response?.status === 404) {
          console.log('CRM connections endpoint not yet implemented');
          setCrmConnections({});
        } else {
          console.error('Failed to load CRM connections:', error);
        }
      }
    };

    if (currentUser) {
      loadAndSyncCrmConnections();
    }
  }, [currentUser]);

  // Auto-sync function for all connected CRMs
  const autoSyncAllCrms = async (crmKeys) => {
    try {
      setSyncing(true);
      let totalClients = 0;
      
      for (const crmKey of crmKeys) {
        try {
          const response = await axios.post(
            `/api/clients/me/crm-connections/${crmKey}/sync`,
            {},
            { headers: authHeader() }
          );
          
          // Store synced data in sessionStorage for campaign use
          const syncedData = response.data;
          if (syncedData?.clients) {
            sessionStorage.setItem(`crm_${crmKey}_clients`, JSON.stringify(syncedData.clients));
            totalClients += syncedData.clients.length;
          }
        } catch (error) {
          console.log(`Auto-sync failed for ${crmKey}:`, error.message);
        }
      }
      
      setLastSyncTime(new Date());
      if (totalClients > 0) {
        console.log(`Auto-synced ${totalClients} clients from ${crmKeys.length} CRM(s)`);
      }
    } catch (error) {
      console.error('Auto-sync error:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleConnect = async (crmKey) => {
    try {
      setLoading(true);
      
      // Initiate OAuth flow - redirect to CRM authorization
      const response = await axios.post(
        `/api/clients/me/crm-connections/${crmKey}/oauth/initiate`,
        { returnUrl: window.location.href },
        { headers: authHeader() }
      );
      
      if (response.data?.authUrl) {
        // Store the CRM key in sessionStorage to handle callback
        sessionStorage.setItem('crm_oauth_pending', crmKey);
        
        // Redirect to CRM OAuth page
        window.location.href = response.data.authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
      
    } catch (error) {
      console.error('Failed to initiate OAuth for CRM:', error);
      if (error.response?.status === 404) {
        message.error(`OAuth integration for ${crmPlatforms.find(p => p.key === crmKey)?.name} is not yet configured. Please contact support.`);
      } else {
        message.error('Failed to connect CRM. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (crmKey) => {
    try {
      setLoading(true);
      
      // Simulate successful disconnection for demo purposes
      setCrmConnections(prev => {
        const newConnections = { ...prev };
        delete newConnections[crmKey];
        return newConnections;
      });
      
      message.success(`${crmPlatforms.find(p => p.key === crmKey)?.name} disconnected successfully!`);
      
      // In production:
      // await axios.delete(`/api/clients/me/crm-connections/${crmKey}`, {
      //   headers: authHeader()
      // });
      
    } catch (error) {
      if (error.response?.status === 404) {
        console.log(`CRM disconnect endpoint not yet implemented for ${crmKey}`);
      } else {
        console.error('Failed to disconnect CRM:', error);
        message.error('Failed to disconnect CRM. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (crmKey) => {
    try {
      setLoading(true);
      
      const response = await axios.post(
        `/api/clients/me/crm-connections/${crmKey}/sync`,
        {},
        { headers: authHeader() }
      );
      
      // Store synced data in sessionStorage (not permanent storage)
      if (response.data?.clients) {
        sessionStorage.setItem(`crm_${crmKey}_clients`, JSON.stringify(response.data.clients));
        setLastSyncTime(new Date());
        message.success(`Synced ${response.data.clients.length} clients from ${crmPlatforms.find(p => p.key === crmKey)?.name}!`);
      } else {
        message.success(`Synced data from ${crmPlatforms.find(p => p.key === crmKey)?.name}!`);
      }
      
    } catch (error) {
      console.error('Failed to sync CRM:', error);
      if (error.response?.status === 404) {
        message.error(`Sync endpoint for ${crmPlatforms.find(p => p.key === crmKey)?.name} is not configured. Please contact support.`);
      } else if (error.response?.status === 401) {
        message.error('CRM connection expired. Please reconnect your account.');
      } else {
        message.error('Failed to sync CRM data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={4}>CRM Integrations</Title>
            <Paragraph type="secondary">
              Connect your CRM platforms to automatically sync client lists and enable targeted campaigns via SMS and email.
            </Paragraph>
          </div>
          {syncing && (
            <div style={{ textAlign: 'center' }}>
              <Spin indicator={<SyncOutlined spin style={{ fontSize: 24 }} />} />
              <div style={{ marginTop: 8, fontSize: 12, color: '#1890ff' }}>Auto-syncing...</div>
            </div>
          )}
        </div>
        {lastSyncTime && (
          <div style={{ marginTop: 16, padding: '12px', background: '#f0f5ff', borderRadius: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              Last synced: {lastSyncTime.toLocaleString()}
            </Text>
            <Paragraph type="secondary" style={{ fontSize: 11, margin: '4px 0 0 0' }}>
              CRM data is automatically synced on login and available for your campaigns. Data is stored in your browser session only.
            </Paragraph>
          </div>
        )}
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        {crmPlatforms.map((platform) => {
          const connection = crmConnections[platform.key];
          const isConnected = connection?.connected;

          return (
            <Col xs={24} sm={12} lg={8} key={platform.key}>
              <Card
                hoverable
                style={{
                  borderColor: isConnected ? platform.color : '#d9d9d9',
                  borderWidth: isConnected ? 2 : 1
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Avatar
                    size={64}
                    src={platform.logo}
                    style={{
                      backgroundColor: !platform.logo ? platform.color : undefined,
                      border: isConnected ? `2px solid ${platform.color}` : 'none'
                    }}
                  >
                    {platform.name.charAt(0)}
                  </Avatar>
                  <div style={{ marginTop: 8 }}>
                    <Space>
                      {isConnected ? (
                        <>
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          <Text strong style={{ color: '#52c41a' }}>Connected</Text>
                        </>
                      ) : (
                        <>
                          <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                          <Text type="secondary">Not Connected</Text>
                        </>
                      )}
                    </Space>
                  </div>
                </div>

                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Title level={5} style={{ margin: 0 }}>{platform.name}</Title>
                  <Paragraph type="secondary" style={{ fontSize: 12, margin: '8px 0' }}>
                    {platform.description}
                  </Paragraph>
                  {isConnected && connection.connectedAt && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Connected: {new Date(connection.connectedAt).toLocaleDateString()}
                    </Text>
                  )}
                </div>

                <div style={{ textAlign: 'center' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {!isConnected ? (
                      <Button
                        type="primary"
                        icon={<LinkOutlined />}
                        onClick={() => handleConnect(platform.key)}
                        loading={loading}
                        style={{ width: '100%', backgroundColor: platform.color, borderColor: platform.color }}
                      >
                        Connect {platform.name}
                      </Button>
                    ) : (
                      <>
                        <Button
                          type="default"
                          icon={<LinkOutlined />}
                          onClick={() => handleSync(platform.key)}
                          loading={loading}
                          style={{ width: '100%' }}
                        >
                          Sync Clients
                        </Button>
                        <Button
                          danger
                          icon={<DisconnectOutlined />}
                          onClick={() => handleDisconnect(platform.key)}
                          loading={loading}
                          style={{ width: '100%' }}
                        >
                          Disconnect
                        </Button>
                      </>
                    )}
                  </Space>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card style={{ marginTop: 24 }}>
        <Title level={5}>How CRM Integration Works</Title>
        <Divider />
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <Avatar size={48} style={{ backgroundColor: '#1890ff', marginBottom: 16 }}>
                1
              </Avatar>
              <Title level={5}>Connect Your CRM</Title>
              <Paragraph type="secondary">
                Click "Connect" on your CRM platform to authorize access and establish the connection.
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <Avatar size={48} style={{ backgroundColor: '#52c41a', marginBottom: 16 }}>
                2
              </Avatar>
              <Title level={5}>Sync Client Data</Title>
              <Paragraph type="secondary">
                Automatically import your client lists and contact information from your CRM.
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ textAlign: 'center' }}>
              <Avatar size={48} style={{ backgroundColor: '#faad14', marginBottom: 16 }}>
                3
              </Avatar>
              <Title level={5}>Launch Campaigns</Title>
              <Paragraph type="secondary">
                Create targeted SMS and email campaigns that notify clients immediately.
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default CRMAuth;
